import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@launchpad-ui/components';

import { Card, ErrorMessage, Field, PageHeader } from '../components/ui';
import { useUpdateQaAssignment } from '../lib/hooks/useQaAssignments';
import { type ReviewSubmission, useSubmitReview } from '../lib/hooks/useReviews';
import type { RubricKey } from '../lib/types';

import styles from '../styles/common.module.css';

type Criterion = Readonly<{
  key: RubricKey;
  label: string;
  points: number;
  options: ReadonlyArray<'true' | 'false' | 'true_na' | 'partial'>;
  note?: string;
  bullets: ReadonlyArray<string>;
}>;

const CRITERIA: ReadonlyArray<{ section: string; items: ReadonlyArray<Criterion> }> = [
  {
    section: 'Soft Skills',
    items: [
      {
        key: 'voice_tone',
        label: 'Voice / Tone',
        points: 5,
        options: ['true', 'false'],
        bullets: [
          'Matched the tone/urgency of the customer.',
          'Maintained a polite, friendly but professional tone throughout the interaction.',
          'Used collaborative language; "Us vs. the Problem."',
          'No profanity or other unprofessional language.',
          'Writing was focused and empowering, refreshingly real, and not arrogant.',
          'Avoided short, choppy sentences that could be perceived as rude except when matching customer tone.',
        ],
      },
      {
        key: 'grammar',
        label: 'Grammar',
        points: 5,
        options: ['true', 'false'],
        bullets: [
          'Used complete sentences whenever possible.',
          'Used punctuation appropriately.',
          'Organized thoughts in a cohesive way making the message easy to read and understand.',
        ],
      },
      {
        key: 'empathy',
        label: 'Empathy',
        points: 5,
        options: ['true_na', 'false'],
        note: 'If necessary:',
        bullets: [
          'Offered a sincere apology to the customer regarding a negative experience.',
          'Provided empathy if the customer expresses any concerns or frustrations.',
          'Acknowledged a long wait-time and apologized for delays.',
        ],
      },
      {
        key: 'opening',
        label: 'Opening',
        points: 2,
        options: ['true', 'false'],
        bullets: [
          'Addressed the customer by name.',
          'If transferred from another department, thanked the person who looped in support.',
        ],
      },
      {
        key: 'closing',
        label: 'Closing',
        points: 3,
        options: ['true', 'false'],
        bullets: [
          'Thanked the customer at the end of the conversation.',
          'Pointed them where to go for additional questions.',
        ],
      },
    ],
  },
  {
    section: 'Problem Solving',
    items: [
      {
        key: 'collect_data',
        label: 'Collect Data',
        points: 5,
        options: ['true', 'false'],
        bullets: [
          'Asked effective probing questions to collect the necessary information.',
          'Identified the steps already taken, acknowledged customer work.',
        ],
      },
      {
        key: 'troubleshooting',
        label: 'Troubleshooting',
        points: 20,
        options: ['true', 'partial', 'false'],
        bullets: [
          'Provided accurate steps and relevant supporting documentation.',
          'Understood the full scope of the issue.',
        ],
      },
      {
        key: 'provide_why',
        label: "Provide the 'Why'",
        points: 10,
        options: ['true', 'partial', 'false'],
        bullets: [
          'Explained the "why" behind troubleshooting steps when appropriate.',
          'Provided a resolution or instructions in a way that was easy to understand.',
        ],
      },
      {
        key: 'efficiency',
        label: 'Efficiency',
        points: 15,
        options: ['true', 'partial', 'false'],
        bullets: [
          'Followed the most efficient path to resolve the issue.',
          "Didn't ask the customer to do unnecessary steps.",
        ],
      },
      {
        key: 'resolution',
        label: 'Resolution',
        points: 10,
        options: ['true', 'partial', 'false'],
        bullets: [
          'Provided a resolution or instructions to set the customer up for success.',
          "All of the customer's queries were addressed.",
        ],
      },
    ],
  },
  {
    section: 'Procedures',
    items: [
      {
        key: 'slo',
        label: 'SLO',
        points: 5,
        options: ['true', 'false'],
        bullets: [
          'Updated customer accordingly to meet SLO.',
          'Updated customer with an expected timeframe for the next response.',
        ],
      },
      {
        key: 'escalation',
        label: 'Escalation / Feature Request / Admin / Transfers',
        points: 5,
        options: ['true_na', 'false'],
        note: 'If necessary:',
        bullets: [
          'Issue properly escalated (when necessary).',
          'Created subsequent Shortcut ticket or Pendo request.',
          'Followed the internal process correctly.',
        ],
      },
      {
        key: 'zendesk',
        label: 'Zendesk',
        points: 5,
        options: ['true', 'false'],
        bullets: [
          'Applied the correct Product Category and Resolution Reason.',
          'Filled out all mandatory fields correctly.',
        ],
      },
      {
        key: 'document',
        label: 'Document',
        points: 5,
        options: ['true', 'false'],
        bullets: [
          'Added internal notes to properly document the interaction.',
          'Fill out knowledge capturing as necessary.',
        ],
      },
    ],
  },
];

const OPTION_LABELS: Record<string, string> = {
  true: 'True',
  false: 'False',
  true_na: 'True / NA',
  partial: 'Partial',
};

const POINT_VALUES: Record<RubricKey, Partial<Record<string, number>>> = {
  voice_tone: { true: 5, false: 0 },
  grammar: { true: 5, false: 0 },
  empathy: { true_na: 5, false: 0 },
  opening: { true: 2, false: 0 },
  closing: { true: 3, false: 0 },
  collect_data: { true: 5, false: 0 },
  troubleshooting: { true: 20, partial: 10, false: 0 },
  provide_why: { true: 10, partial: 5, false: 0 },
  efficiency: { true: 15, partial: 7.5, false: 0 },
  resolution: { true: 10, partial: 5, false: 0 },
  slo: { true: 5, false: 0 },
  escalation: { true_na: 5, false: 0 },
  zendesk: { true: 5, false: 0 },
  document: { true: 5, false: 0 },
};

type Answers = Partial<Record<RubricKey, string>>;
const EMPTY_ANSWERS: Answers = Object.fromEntries(
  CRITERIA.flatMap((s) => s.items.map((i) => [i.key, '' as string])),
) as Answers;

function calcScore(answers: Answers): number {
  return (Object.keys(POINT_VALUES) as RubricKey[]).reduce((sum, key) => {
    const ans = answers[key];
    if (!ans) return sum;
    return sum + (POINT_VALUES[key][ans] ?? 0);
  }, 0);
}

export function ReviewForm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const assignmentId = params.get('assignment');

  const [header, setHeader] = useState({
    reviewer_email: '',
    tse_name: params.get('tse') ?? '',
    ticket_number: params.get('ticket') ?? '',
    ticket_topic: params.get('topic') ?? '',
  });
  const [answers, setAnswers] = useState<Answers>(EMPTY_ANSWERS);
  const [feedback, setFeedback] = useState({ constructive: '', positive: '' });
  const [submitted, setSubmitted] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const submitMutation = useSubmitReview();
  const updateAssignment = useUpdateQaAssignment();

  const score = calcScore(answers);
  const allAnswered = (Object.keys(POINT_VALUES) as RubricKey[]).every((k) => answers[k]);
  const headerComplete = !!(header.reviewer_email && header.tse_name && header.ticket_number);
  const feedbackComplete = !!(feedback.constructive.trim() && feedback.positive.trim());
  const canSubmit = allAnswered && headerComplete && feedbackComplete;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setWarning(null);
    const payload: ReviewSubmission = {
      ...header,
      ...(answers as Record<RubricKey, string>),
      constructive_feedback: feedback.constructive,
      positive_feedback: feedback.positive,
    };
    let res;
    try {
      res = await submitMutation.mutateAsync(payload);
    } catch (err) {
      setError((err as Error).message);
      return;
    }
    if (assignmentId) {
      try {
        await updateAssignment.mutateAsync({
          id: Number(assignmentId),
          patch: { status: 'completed' },
        });
      } catch (err) {
        setWarning(
          `Review saved, but the queue item could not be marked complete: ${(err as Error).message}. Update it manually from the Queue.`,
        );
      }
    }
    setSubmitted(res.score);
  };

  const reset = () => {
    setHeader({ reviewer_email: '', tse_name: '', ticket_number: '', ticket_topic: '' });
    setAnswers(EMPTY_ANSWERS);
    setFeedback({ constructive: '', positive: '' });
    setSubmitted(null);
    setError(null);
    setWarning(null);
  };

  if (submitted !== null) {
    return (
      <div className={styles.pageNarrow} style={{ paddingTop: '4rem', textAlign: 'center' }}>
        <Card>
          <div style={{ fontSize: '4rem', fontWeight: 700 }}>{submitted}</div>
          <div style={{ color: 'var(--lp-color-text-ui-secondary)', marginBottom: '1.5rem' }}>out of 100</div>
          <p style={{ marginBottom: '1.5rem' }}>Review submitted.</p>
          {warning && (
            <p
              style={{
                marginBottom: '1.5rem',
                color: 'var(--lp-color-text-feedback-warning, #92400e)',
              }}
            >
              {warning}
            </p>
          )}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
            {assignmentId && (
              <Button variant="minimal" onPress={() => navigate('/queue')}>
                Back to Queue
              </Button>
            )}
            <Button onPress={reset}>Submit Another Review</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.pageNarrow}>
      <PageHeader title="Ticket Review" subtitle={`Score: ${score} / 100`} />

      <form onSubmit={submit}>
        <Card>
          <h2 className={styles.sectionTitle}>Review Details</h2>
          <div className={styles.formGrid}>
            <Field label="Your Email" required>
              <input
                className={styles.input}
                type="email"
                required
                value={header.reviewer_email}
                onChange={(e) => setHeader((h) => ({ ...h, reviewer_email: e.target.value }))}
              />
            </Field>
            <Field label="TSE Being Reviewed" required>
              <input
                className={styles.input}
                type="text"
                required
                value={header.tse_name}
                onChange={(e) => setHeader((h) => ({ ...h, tse_name: e.target.value }))}
              />
            </Field>
            <Field label="Zendesk Ticket #" required>
              <input
                className={styles.input}
                type="text"
                required
                value={header.ticket_number}
                onChange={(e) => setHeader((h) => ({ ...h, ticket_number: e.target.value }))}
              />
            </Field>
            <Field label="Ticket Topic">
              <input
                className={styles.input}
                type="text"
                value={header.ticket_topic}
                onChange={(e) => setHeader((h) => ({ ...h, ticket_topic: e.target.value }))}
                placeholder="e.g. Flags & Targeting"
              />
            </Field>
          </div>
        </Card>

        {CRITERIA.map(({ section, items }) => (
          <Card key={section}>
            <h2 className={styles.sectionTitle}>{section}</h2>
            {items.map((item) => {
              const earned = answers[item.key] ? (POINT_VALUES[item.key][answers[item.key]!] ?? null) : null;
              return (
                <div
                  key={item.key}
                  style={{
                    padding: '1rem 0',
                    borderBottom: '1px solid var(--lp-color-border-ui-secondary)',
                  }}
                >
                  <div className={styles.spaceBetween}>
                    <div style={{ fontWeight: 500 }}>
                      {item.label}
                      <span className={styles.required}>*</span>
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: 'var(--lp-color-text-ui-secondary)',
                      }}
                    >
                      {item.points} points
                      {earned !== null && <span> (earned {earned})</span>}
                    </div>
                  </div>
                  {item.note && (
                    <p
                      style={{
                        margin: '0.5rem 0',
                        fontSize: '0.8rem',
                        color: 'var(--lp-color-text-ui-secondary)',
                      }}
                    >
                      {item.note}
                    </p>
                  )}
                  <ul style={{ margin: '0.5rem 0 0.75rem 0', paddingLeft: '1.25rem' }}>
                    {item.bullets.map((b, i) => (
                      <li key={i} style={{ fontSize: '0.8rem', color: 'var(--lp-color-text-ui-secondary)' }}>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    {item.options.map((opt) => (
                      <label
                        key={opt}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="radio"
                          name={item.key}
                          value={opt}
                          checked={answers[item.key] === opt}
                          onChange={() => setAnswers((a) => ({ ...a, [item.key]: opt }))}
                        />
                        <span style={{ fontSize: '0.85rem' }}>{OPTION_LABELS[opt]}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </Card>
        ))}

        <Card>
          <h2 className={styles.sectionTitle}>Feedback</h2>
          <Field label="Constructive Feedback" required>
            <textarea
              className={styles.textarea}
              rows={4}
              required
              value={feedback.constructive}
              onChange={(e) => setFeedback((f) => ({ ...f, constructive: e.target.value }))}
            />
          </Field>
          <div style={{ height: '1rem' }} />
          <Field label="Positive Feedback" required>
            <textarea
              className={styles.textarea}
              rows={4}
              required
              value={feedback.positive}
              onChange={(e) => setFeedback((f) => ({ ...f, positive: e.target.value }))}
            />
          </Field>
        </Card>

        <ErrorMessage message={error} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem 0 2rem 0',
          }}
        >
          <span style={{ fontSize: '0.85rem', color: 'var(--lp-color-text-ui-secondary)' }}>
            {!allAnswered && 'Answer all criteria to submit.'}
            {allAnswered && !headerComplete && 'Fill in reviewer details to submit.'}
            {allAnswered && headerComplete && !feedbackComplete && 'Add both feedback sections to submit.'}
          </span>
          <Button type="submit" isDisabled={submitMutation.isPending || !canSubmit}>
            {submitMutation.isPending ? 'Submitting…' : `Submit Review · ${score}/100`}
          </Button>
        </div>
      </form>
    </div>
  );
}
